// app/frontend/components/ui/Pagination.tsx
import { Pagination as MantinePagination, Box, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { useMemo } from "react";

export interface PaginationProps {
  /**
   * Total number of pages
   */
  total: number;
  /**
   * Current active page (1-indexed)
   */
  value: number;
  /**
   * Callback fired when page changes
   */
  onChange: (page: number) => void;
  /**
   * Items per page (used for displaying count)
   */
  itemsPerPage?: number;
  /**
   * Total number of items (used for displaying count)
   */
  totalItems?: number;
  /**
   * Show item count info (e.g., "Showing 1-12 of 100")
   */
  showItemsInfo?: boolean;
  /**
   * Custom color theme (defaults to orange)
   */
  color?: string;
  /**
   * Size of pagination controls
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Additional CSS styles
   */
  style?: React.CSSProperties;
}

/**
 * ModernPagination
 *
 * A modern, reusable pagination component using Mantine's Pagination
 * with custom styling that matches the app's dark theme and orange accents.
 *
 * Features:
 * - Responsive design (fewer controls on mobile)
 * - Optional item count display
 * - Smooth animations and transitions
 * - Accessible (ARIA labels, keyboard navigation)
 * - Consistent with app design system
 *
 * @example
 * <ModernPagination
 *   total={10}
 *   value={1}
 *   onChange={(page) => setPage(page)}
 *   totalItems={100}
 *   itemsPerPage={12}
 *   showItemsInfo={true}
 * />
 */
export function ModernPagination({
  total,
  value,
  onChange,
  itemsPerPage,
  totalItems,
  showItemsInfo = false,
  color = "orange",
  size = "md",
  style,
}: PaginationProps) {
  // Don't render if there's only one page or no pages
  if (total <= 1) return null;

  // Detect mobile screen size for responsive sizing
  const isMobile = useMediaQuery("(max-width: 640px)");

  // Unique ID for scoped styling (CSS-safe)
  const paginationId = useMemo(
    () => `pagination-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  // Use smaller size on mobile, otherwise use provided size
  const responsiveSize = isMobile ? "sm" : size;

  // Calculate item range for display
  const startItem =
    totalItems && itemsPerPage ? (value - 1) * itemsPerPage + 1 : null;
  const endItem =
    totalItems && itemsPerPage
      ? Math.min(value * itemsPerPage, totalItems)
      : null;

  return (
    <>
      {/* Scoped CSS styles for pagination */}
      <style>
        {`
          #pagination-${paginationId} .mantine-Pagination-control {
            background-color: rgba(255, 255, 255, 0.05) !important;
            border: 1px solid rgba(255, 152, 0, 0.2) !important;
            color: #F0F0F0 !important;
            transition: all 0.2s ease !important;
          }
          
          #pagination-${paginationId} .mantine-Pagination-control:hover:not([data-disabled]) {
            background-color: rgba(255, 152, 0, 0.15) !important;
            border-color: rgba(255, 152, 0, 0.4) !important;
            transform: translateY(-1px) !important;
          }
          
          #pagination-${paginationId} .mantine-Pagination-control[data-active] {
            background-color: #FF9800 !important;
            border-color: #FF9800 !important;
            color: #171717 !important;
            font-weight: 600 !important;
            box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3) !important;
          }
          
          #pagination-${paginationId} .mantine-Pagination-control[data-active]:hover {
            background-color: #FFB74D !important;
            border-color: #FFB74D !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4) !important;
          }
          
          #pagination-${paginationId} .mantine-Pagination-control[data-disabled] {
            opacity: 0.4 !important;
            cursor: not-allowed !important;
          }
          
          #pagination-${paginationId} .mantine-Pagination-dots {
            color: rgba(255, 255, 255, 0.5) !important;
          }
        `}
      </style>

      <Box
        id={`pagination-${paginationId}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          width: "100%",
          padding: "16px 8px",
          ...style,
        }}
      >
        {/* Item count info */}
        {showItemsInfo && startItem && endItem && totalItems && (
          <Text
            size="sm"
            c="dimmed"
            style={{
              fontSize: "clamp(0.75rem, 2.5vw, 0.875rem)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            Showing {startItem}-{endItem} of {totalItems}
          </Text>
        )}

        {/* Pagination controls */}
        <MantinePagination
          total={total}
          value={value}
          onChange={onChange}
          color={color}
          size={responsiveSize}
          radius="md"
          withEdges
          siblings={isMobile ? 0 : 1}
          boundaries={isMobile ? 0 : 1}
          styles={{
            root: {
              fontFamily: "'Space Grotesk', sans-serif",
            },
          }}
        />
      </Box>
    </>
  );
}

export default ModernPagination;
